import React from 'react';
import styled, { css } from 'styled-components';
import { GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_3_SCREEN_WIDTH_MAX } from '@bbc/gel-foundations/breakpoints';
import { GEL_SPACING, GEL_SPACING_DBL } from '@bbc/gel-foundations/spacings';
import { C_WHITE, C_POSTBOX, C_SHADOW, C_EBON, C_LUNAR } from '@bbc/psammead-styles/colours';
import { string, oneOf, node, bool, shape } from 'prop-types';
import { getSansRegular, getSerifMedium } from '@bbc/psammead-styles/font-styles';
import { scriptPropType } from '@bbc/gel-foundations/prop-types';
import { getPica, getGreatPrimer, getLongPrimer } from '@bbc/gel-foundations/typography';
import { mediaIcons } from '@bbc/psammead-assets/svgs';
import LiveLabel from '@bbc/psammead-live-label';
import VisuallyHiddenText from '@bbc/psammead-visually-hidden-text';
import { Link } from '@bbc/psammead-story-promo';
import ImageGridItem from './ImageStyles';
import TextGridItem from './TextStyles';
var bulletinWrapperStyles = "\n  position: relative;\n  background-color: ".concat(C_LUNAR, ";\n  display: grid;\n  grid-template-columns: repeat(6, 1fr);\n  grid-column-gap: ").concat(GEL_SPACING_DBL, ";\n");
var RadioBulletinWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "RadioBulletinWrapper",
  componentId: "sc-5q1cdz-0"
})(["", ";background-color:", ";"], bulletinWrapperStyles, C_LUNAR);
var TVBulletinWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TVBulletinWrapper",
  componentId: "sc-5q1cdz-1"
})(["", ";@media (min-width:", "){padding:", ";}"], bulletinWrapperStyles, GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_SPACING_DBL);
var headingStyles = /*#__PURE__*/css(["color:", ";margin:0;padding:", ";", ""], C_EBON, GEL_SPACING, function (_ref) {
  var service = _ref.service;
  return service && getSerifMedium(service);
});
var radioHeading = /*#__PURE__*/css(["", " @media (min-width:", ") and (max-width:", "){padding-top:", ";padding-bottom:", ";", "}"], function (_ref2) {
  var script = _ref2.script;
  return script && getPica(script);
}, GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_3_SCREEN_WIDTH_MAX, GEL_SPACING, GEL_SPACING, function (_ref3) {
  var dir = _ref3.dir;
  return dir === 'ltr' ? "padding-left: 0;" : "padding-right: 0;";
});
var tvHeading = /*#__PURE__*/css(["", " @media (min-width:", "){padding:0 0 ", " 0;}"], function (_ref4) {
  var script = _ref4.script;
  return script && getGreatPrimer(script);
}, GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_SPACING);
var bulletinHeadinStyles = {
  radio: radioHeading,
  tv: tvHeading
};
var BulletinHeading = /*#__PURE__*/styled.h3.withConfig({
  displayName: "BulletinHeading",
  componentId: "sc-5q1cdz-2"
})(["", " ", ""], headingStyles, function (_ref5) {
  var bulletinType = _ref5.bulletinType;
  return bulletinHeadinStyles[bulletinType];
});
var radioSummary = /*#__PURE__*/css(["@media (min-width:", ") and (max-width:", "){", "}"], GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_3_SCREEN_WIDTH_MAX, function (_ref6) {
  var dir = _ref6.dir;
  return dir === 'ltr' ? 'padding-left: 0;' : 'padding-right: 0;';
});
var tvSummary = /*#__PURE__*/css(["@media (min-width:", "){", "}"], GEL_GROUP_3_SCREEN_WIDTH_MIN, function (_ref7) {
  var dir = _ref7.dir,
      bulletinType = _ref7.bulletinType;
  return dir === 'ltr' ? css(["padding-left:0;", ""], bulletinType === 'tv' && 'padding-right: 0;') : css(["padding-right:0;", ""], bulletinType === 'tv' && 'padding-left: 0;');
});
var bulletinSummaryStyles = {
  radio: radioSummary,
  tv: tvSummary
};
var BulletinSummary = /*#__PURE__*/styled.p.withConfig({
  displayName: "BulletinSummary",
  componentId: "sc-5q1cdz-3"
})(["color:", ";margin:0;padding:0 ", " ", ";", " ", "  ", ""], C_SHADOW, GEL_SPACING, GEL_SPACING_DBL, function (_ref8) {
  var script = _ref8.script;
  return script && getLongPrimer(script);
}, function (_ref9) {
  var service = _ref9.service;
  return service && getSansRegular(service);
}, function (_ref10) {
  var bulletinType = _ref10.bulletinType;
  return bulletinSummaryStyles[bulletinType];
});
var IconWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "IconWrapper",
  componentId: "sc-5q1cdz-4"
})(["display:flex;align-items:center;> svg{color:", ";fill:currentColor;width:1.0625rem;height:", ";margin:0;}", ""], C_WHITE, GEL_SPACING_DBL, function (_ref11) {
  var dir = _ref11.dir;
  return dir === 'ltr' ? "padding-right: ".concat(GEL_SPACING, ";") : "padding-left: ".concat(GEL_SPACING, ";");
});
var radioPlayCta = /*#__PURE__*/css(["@media (min-width:", ") and (max-width:", "){display:inline-flex;padding:", " ", ";margin-bottom:", ";}"], GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_3_SCREEN_WIDTH_MAX, GEL_SPACING, GEL_SPACING_DBL, GEL_SPACING_DBL);
var tvPlayCta = /*#__PURE__*/css(["@media (min-width:", "){display:inline-flex;padding:", " ", ";}"], GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_SPACING, GEL_SPACING_DBL);
var playCtaStyles = {
  radio: radioPlayCta,
  tv: tvPlayCta
};
var PlayCTA = /*#__PURE__*/styled.div.attrs({
  'aria-hidden': true
}).withConfig({
  displayName: "PlayCTA",
  componentId: "sc-5q1cdz-5"
})(["background-color:", ";border:0.0625rem solid transparent;color:", ";padding:0.75rem;display:flex;align-items:center;justify-content:center;", ";", ";", ""], function (_ref12) {
  var isLive = _ref12.isLive;
  return isLive ? C_POSTBOX : C_EBON;
}, C_WHITE, function (_ref13) {
  var script = _ref13.script;
  return script && getPica(script);
}, function (_ref14) {
  var service = _ref14.service;
  return service && getSansRegular(service);
}, function (_ref15) {
  var bulletinType = _ref15.bulletinType;
  return playCtaStyles[bulletinType];
});

var Bulletin = function Bulletin(_ref16) {
  var script = _ref16.script,
      service = _ref16.service,
      dir = _ref16.dir,
      image = _ref16.image,
      mediaType = _ref16.mediaType,
      headlineText = _ref16.headlineText,
      summaryText = _ref16.summaryText,
      ctaLink = _ref16.ctaLink,
      ctaText = _ref16.ctaText,
      isLive = _ref16.isLive,
      liveText = _ref16.liveText,
      offScreenText = _ref16.offScreenText,
      lang = _ref16.lang;
  var isAudio = mediaType === 'audio';
  var bulletinType = isAudio ? 'radio' : 'tv';
  var BulletinWrapper = isAudio ? RadioBulletinWrapper : TVBulletinWrapper;
  return /*#__PURE__*/React.createElement(BulletinWrapper, null, image && /*#__PURE__*/React.createElement(ImageGridItem, {
    bulletinType: bulletinType
  }, image), /*#__PURE__*/React.createElement(TextGridItem, {
    bulletinType: bulletinType,
    fullWidth: !image,
    dir: dir
  }, /*#__PURE__*/React.createElement(BulletinHeading, {
    script: script,
    service: service,
    bulletinType: bulletinType,
    dir: dir
  }, /*#__PURE__*/React.createElement(Link, {
    href: ctaLink
  }, isLive ? /*#__PURE__*/React.createElement(LiveLabel, {
    service: service,
    dir: dir,
    liveText: liveText,
    ariaHidden: true,
    offScreenText: offScreenText
  }, headlineText) :
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/aria-role
  React.createElement("span", {
    role: "text"
  }, offScreenText && /*#__PURE__*/React.createElement(VisuallyHiddenText, {
    lang: lang
  }, "".concat(offScreenText, ", ")), /*#__PURE__*/React.createElement("span", null, headlineText)))), summaryText && /*#__PURE__*/React.createElement(BulletinSummary, {
    script: script,
    service: service,
    bulletinType: bulletinType,
    dir: dir
  }, summaryText), /*#__PURE__*/React.createElement(PlayCTA, {
    isLive: isLive,
    service: service,
    script: script,
    bulletinType: bulletinType,
    dir: dir
  }, /*#__PURE__*/React.createElement(IconWrapper, {
    dir: dir
  }, mediaIcons[mediaType]), ctaText)));
};

Bulletin.propTypes = {
  mediaType: oneOf(['video', 'audio']).isRequired,
  service: string.isRequired,
  script: shape(scriptPropType).isRequired,
  dir: oneOf(['ltr', 'rtl']),
  ctaText: string.isRequired,
  ctaLink: string.isRequired,
  image: node,
  summaryText: string,
  headlineText: string.isRequired,
  isLive: bool,
  liveText: string,
  offScreenText: string.isRequired,
  lang: string
};
Bulletin.defaultProps = {
  dir: 'ltr',
  image: null,
  summaryText: null,
  isLive: false,
  liveText: 'LIVE',
  lang: null
};
export default Bulletin;